import { useEffect, useRef, useState } from "react";
import { Button, Input, Typography, Spin, Steps, message } from "antd";
import { useOrg } from "src/hooks/useOrg";
import {
  createSource,
  getSource,
  updateSource,
} from "pages/api-clients/integrations";
import {
  Source,
  SourceType,
} from "pages/api/[orgId]/integrations-client/definitions";

enum IntegrationState {
  fetching,
  creating,
  saving,
  none,
  active,
}

export function CircleCiIntegrationStatus() {
  const { id: orgId } = useOrg();
  const [source, setSource] = useState<Source | null>(null);
  const [state, setState] = useState<IntegrationState>(
    IntegrationState.fetching
  );
  const [authToken, setAuthToken] = useState("");
  const fetchedOnce = useRef(false);

  const listItems = [
    {
      description: (
        <p>
          Log in to your CircleCI account at{" "}
          <a
            href="https://circleci.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://circleci.com
          </a>{" "}
          using your credentials.
        </p>
      ),
    },
    {
      description:
        "Once you're logged in, navigate to your account settings (e.g. by clicking on avatar image in the bottom left). Look for a section related to 'API Tokens' or 'Personal API Tokens.'",
    },
    {
      description:
        "In the API Tokens section, there should be an option to generate a new token. Click on that option and provide a name for the token. This name is usually for your reference to remember the purpose of the token.",
    },
    { description: "Copy the token and paste it here." },
    { description: "Click Save" },
  ];

  useEffect(() => {
    if (!fetchedOnce.current) {
      return;
    }

    if (source == null) {
      setState(IntegrationState.none);
    } else {
      setState(IntegrationState.active);
    }
  }, [source]);

  const transitionRef = useRef({ source, orgId });
  transitionRef.current = { source, orgId };
  useEffect(() => {
    const { source, orgId } = transitionRef.current;
    if (!orgId) return;
    switch (state) {
      case IntegrationState.fetching: {
        getSource(orgId, SourceType.CircleCi).then((payload) => {
          const circleCiSource = payload.data?.[0];
          fetchedOnce.current = true;
          if (circleCiSource == null) {
            setState(IntegrationState.none);
          } else {
            setSource(circleCiSource);
            setAuthToken(circleCiSource?.authToken || "");
            setState(IntegrationState.active);
          }
        });
        break;
      }
      case IntegrationState.creating: {
        createSource(orgId, SourceType.CircleCi).then((payload) => {
          setSource(payload.data);
          setState(IntegrationState.fetching);
        });
        break;
      }

      case IntegrationState.saving: {
        updateSource(orgId, SourceType.CircleCi, {
          id: source!.id,
          authToken,
        }).then((payload) => {
          setSource(payload.data);
          setAuthToken(payload.data?.authToken || "");
          setState(IntegrationState.fetching);
          message.success("Successfully updated circleci integration");
        });
        break;
      }
    }
  }, [state, authToken]);

  return [
    IntegrationState.fetching,
    IntegrationState.creating,
    IntegrationState.saving,
  ].includes(state) ? (
    <Spin />
  ) : state == IntegrationState.none ? (
    <Button
      onClick={() => {
        setState(IntegrationState.creating);
      }}
    >
      Create CircleCi Integration
    </Button>
  ) : (
    <div>
      <Steps
        direction="vertical"
        progressDot
        items={listItems}
        current={listItems.length - 1}
      />
      <Typography.Title level={5}>Configure your token</Typography.Title>
      <Input.Password
        allowClear
        defaultValue={authToken}
        visibilityToggle={authToken != "placeholder"}
        style={{ width: "80%" }}
        onChange={(event) => setAuthToken(event.target.value)}
      />
      <Button
        type="primary"
        style={{ marginLeft: "10px" }}
        onClick={() => {
          setState(IntegrationState.saving);
        }}
        disabled={authToken.length === 0}
      >
        Save
      </Button>
    </div>
  );
}
